/**
 * The one filter set, shared by every tab.
 *
 * Filters live in the URL query string rather than in component state. That is
 * not a nicety: the whole point of this module is that a number is only
 * meaningful next to the slice it came from, and a link that reproduces the
 * slice is what lets an operator send "look at this" to someone else. It also
 * survives a reload, which matters when the answer took four filter changes to
 * find.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Calendar, RotateCw, X } from 'lucide-react';
import { AdminAPI, UsageChannel, UsageFilterOptions, UsageFilterState, UsageModality } from '../../lib/api';
import { SearchSelect, SearchSelectOption } from '../../components/SearchSelect';
import { CHANNEL_LABEL, MODALITY_LABEL, isoDay, useCurrency } from './shared';

export type RangePreset = 'today' | '7d' | '30d' | '90d' | 'mtd' | 'custom';

const daysAgo = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return isoDay(d);
};

/** Read the filter state out of the URL, with sane defaults. */
export function useUsageFilters() {
  const [params, setParams] = useSearchParams();
  const today = isoDay(new Date());

  const filters: UsageFilterState = useMemo(() => ({
    from: params.get('from') || daysAgo(29),
    to: params.get('to') || today,
    organizationId: params.get('org') || undefined,
    agent: params.get('agent') || undefined,
    channel: (params.get('channel') as UsageChannel) || undefined,
    modality: (params.get('modality') as UsageModality) || undefined,
    agentKind: undefined,
    provider: params.get('provider') || undefined,
    feature: params.get('feature') || undefined,
    campaign: params.get('campaign') || undefined,
    customer: params.get('customer') || undefined,
    // URL-backed like everything else here: ?sims=1 keeps the choice in the link.
    includeSimulated: params.get('sims') === '1',
  }), [params, today]);

  const patch = useCallback((next: Partial<Record<string, string | undefined>>) => {
    setParams((prev) => {
      const q = new URLSearchParams(prev);
      for (const [k, v] of Object.entries(next)) {
        if (v) q.set(k, v); else q.delete(k);
      }
      return q;
    }, { replace: true });
  }, [setParams]);

  const preset = (params.get('preset') as RangePreset) || '30d';
  return {
    filters, patch, preset,
    tab: params.get('tab') || 'overview',
    ccy: (params.get('ccy') === 'inr' ? 'INR' : 'USD') as 'USD' | 'INR',
  };
}

/** The filters as the scope chips name them; the URL keys are not labels. */
const FILTER_NAME: Record<string, string> = {
  org: 'organization', agent: 'agent', channel: 'channel', modality: 'cost type',
  provider: 'provider', feature: 'feature', campaign: 'campaign', customer: 'customer',
};

const PRESETS: [RangePreset, string, () => { from: string; to: string }][] = [
  ['today', 'Today', () => ({ from: isoDay(new Date()), to: isoDay(new Date()) })],
  ['7d', '7 days', () => ({ from: daysAgo(6), to: isoDay(new Date()) })],
  ['30d', '30 days', () => ({ from: daysAgo(29), to: isoDay(new Date()) })],
  ['90d', '90 days', () => ({ from: daysAgo(89), to: isoDay(new Date()) })],
  ['mtd', 'This month', () => ({ from: `${isoDay(new Date()).slice(0, 8)}01`, to: isoDay(new Date()) })],
];

export const FilterBar: React.FC<{
  api: AdminAPI;
  filters: UsageFilterState;
  preset: RangePreset;
  patch: (next: Partial<Record<string, string | undefined>>) => void;
  onRefresh: () => void;
  loading: boolean;
  /** Per-agent spend from the current window, shown beside each agent name. */
  agentCost?: Record<string, number>;
}> = ({ api, filters, preset, patch, onRefresh, loading, agentCost }) => {
  const { fmt } = useCurrency();
  const [options, setOptions] = useState<UsageFilterOptions | null>(null);
  const [searching, setSearching] = useState(false);
  const [hits, setHits] = useState<UsageFilterOptions['agents']>([]);
  const [customerHits, setCustomerHits] = useState<UsageFilterOptions['customers']>([]);
  const [searchingCustomers, setSearchingCustomers] = useState(false);
  const [optionsError, setOptionsError] = useState<string | null>(null);
  const [optionsRetry, setOptionsRetry] = useState(0);

  useEffect(() => {
    api.getUsageFilterOptions({ organization_id: filters.organizationId })
      .then((o) => { setOptions(o); setOptionsError(null); })
      .catch((e) => { setOptions(null); setOptionsError(e instanceof Error ? e.message : String(e)); });
  }, [api, filters.organizationId, optionsRetry]);

  // Server-side name search: the loaded roster is a slice, and an operator
  // looking for one agent by name should not have to know that.
  const searchAgents = useCallback(async (q: string) => {
    if (q.trim().length < 2) { setHits([]); return; }
    setSearching(true);
    try {
      const r = await api.getUsageFilterOptions({ q: q.trim(), organization_id: filters.organizationId });
      setHits(r.agents);
    } catch { /* best-effort; local filtering still applies */ }
    finally { setSearching(false); }
  }, [api, filters.organizationId]);

  // The customer book is the one axis too big to ship whole; searching it
  // server-side is the difference between a usable filter and a decorative one.
  const searchCustomers = useCallback(async (q: string) => {
    if (q.trim().length < 2) { setCustomerHits([]); return; }
    setSearchingCustomers(true);
    try {
      const r = await api.getUsageFilterOptions({ q: q.trim(), organization_id: filters.organizationId });
      setCustomerHits(r.customers ?? []);
    } catch { /* best-effort; local filtering still applies */ }
    finally { setSearchingCustomers(false); }
  }, [api, filters.organizationId]);

  const orgOptions: SearchSelectOption[] = useMemo(
    () => (options?.organizations ?? []).map((o) => ({ value: o.id, label: o.name })),
    [options],
  );

  const campaignOptions: SearchSelectOption[] = useMemo(
    () => (options?.campaigns ?? []).map((c) => ({
      value: c.id,
      label: c.name,
      sublabel: [CHANNEL_LABEL[c.channel] ?? c.channel, c.status].filter(Boolean).join(' · '),
    })),
    [options],
  );

  // Customers arrive spend-first from the server, so the default list is the
  // one worth looking at rather than whoever is alphabetically first.
  const customerOptions: SearchSelectOption[] = useMemo(() => {
    const merged = new Map<string, NonNullable<UsageFilterOptions['customers']>[number]>();
    for (const c of options?.customers ?? []) merged.set(c.id, c);
    for (const c of customerHits) if (!merged.has(c.id)) merged.set(c.id, c);
    return Array.from(merged.values()).map((c) => ({
      value: c.id,
      label: c.name || c.phone || c.id.slice(0, 8),
      sublabel: c.name ? c.phone ?? undefined : undefined,
      right: c.cost_usd ? fmt(c.cost_usd) : undefined,
    }));
  }, [options, customerHits, fmt]);

  const agentOptions: SearchSelectOption[] = useMemo(() => {
    const merged = new Map<string, UsageFilterOptions['agents'][number]>();
    for (const a of options?.agents ?? []) merged.set(a.agent_ref, a);
    for (const a of hits) if (!merged.has(a.agent_ref)) merged.set(a.agent_ref, a);
    return Array.from(merged.values()).map((a) => {
      const cost = agentCost?.[a.agent_ref];
      return {
        value: a.agent_ref,
        label: a.name,
        // The channels an agent actually carries (mig 0554) — so it is obvious
        // before filtering whether a phone breakdown will have anything in it.
        sublabel: a.channels?.filter(Boolean).map((c) => CHANNEL_LABEL[c] ?? c).join(' · '),
        right: cost ? fmt(cost) : undefined,
        badge: a.agent_ref.startsWith('cfg:')
          ? { text: 'NAV', className: 'bg-indigo-50 text-indigo-700 border-indigo-200' }
          : { text: 'AGENT', className: 'bg-blue-50 text-blue-700 border-blue-200' },
      };
    });
  }, [options, hits, agentCost, fmt]);

  const active = [
    filters.organizationId && ['org', options?.organizations.find((o) => o.id === filters.organizationId)?.name || 'Organization'],
    filters.agent && ['agent', agentOptions.find((a) => a.value === filters.agent)?.label || filters.agent],
    filters.channel && ['channel', CHANNEL_LABEL[filters.channel] ?? filters.channel],
    filters.modality && ['modality', MODALITY_LABEL[filters.modality] ?? filters.modality],
    filters.provider && ['provider', filters.provider],
    filters.feature && ['feature', filters.feature],
    filters.campaign && ['campaign', campaignOptions.find((c) => c.value === filters.campaign)?.label || 'Campaign'],
    filters.customer && ['customer', customerOptions.find((c) => c.value === filters.customer)?.label || 'Customer'],
  ].filter(Boolean) as [string, string][];

  return (
    <div className="space-y-3 rounded-lg bg-white p-4 shadow">
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-52">
          <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-gray-500">Organization</label>
          <SearchSelect
            value={filters.organizationId || ''}
            onChange={(v) => patch({ org: v || undefined, agent: undefined })}
            options={orgOptions}
            allLabel="All organizations"
            placeholder="Search organizations…"
          />
        </div>

        <div className="w-64">
          <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-gray-500">Agent</label>
          <SearchSelect
            value={filters.agent || ''}
            onChange={(v) => patch({ agent: v || undefined })}
            options={agentOptions}
            allLabel={filters.organizationId ? 'All agents in organization' : 'All agents'}
            placeholder="Search agents by name…"
            onSearch={searchAgents}
            loading={searching}
          />
        </div>

        <div className="w-56">
          <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-gray-500">Campaign</label>
          <SearchSelect
            value={filters.campaign || ''}
            onChange={(v) => patch({ campaign: v || undefined })}
            options={campaignOptions}
            allLabel={filters.organizationId ? 'All campaigns in organization' : 'All campaigns'}
            placeholder="Search campaigns…"
          />
        </div>

        <div className="w-56">
          <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-gray-500">Customer</label>
          <SearchSelect
            value={filters.customer || ''}
            onChange={(v) => patch({ customer: v || undefined })}
            options={customerOptions}
            allLabel={filters.organizationId ? 'All customers in organization' : 'All customers'}
            placeholder="Search by name, phone or email…"
            onSearch={searchCustomers}
            loading={searchingCustomers}
          />
        </div>

        <Select
          label="Channel"
          value={filters.channel || ''}
          onChange={(v) => patch({ channel: v || undefined })}
          allLabel="All channels"
          items={(options?.channels ?? []).map((c) => [c, CHANNEL_LABEL[c] ?? c])}
        />
        <Select
          label="Cost type"
          value={filters.modality || ''}
          onChange={(v) => patch({ modality: v || undefined })}
          allLabel="All cost types"
          items={(options?.modalities ?? []).map((m) => [m, MODALITY_LABEL[m] ?? m])}
        />
        <label
          className="flex items-center gap-1.5 self-end pb-2 text-xs text-gray-600"
          title="Simulator runs mint 'SIM-' call ids: real LLM turns, no audio, no carrier minutes, no customer. They are left out of every figure unless ticked."
        >
          <input
            type="checkbox"
            checked={!!filters.includeSimulated}
            onChange={(e) => patch({ sims: e.target.checked ? '1' : undefined })}
            className="rounded border-gray-300"
          />
          Include simulator calls
        </label>

        <div>
          <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-gray-500">Range</label>
          <div className="inline-flex overflow-hidden rounded-md border border-gray-300 text-sm">
            {PRESETS.map(([key, label, calc]) => (
              <button
                key={key}
                onClick={() => patch({ preset: key, ...calc() })}
                className={`px-2.5 py-1.5 ${preset === key ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-end gap-1.5">
          <Calendar className="mb-2 h-3.5 w-3.5 text-gray-400" />
          <input
            type="date" value={filters.from}
            onChange={(e) => patch({ from: e.target.value, preset: 'custom' })}
            className="rounded border-gray-300 text-sm"
          />
          <span className="pb-2 text-xs text-gray-400">→</span>
          <input
            type="date" value={filters.to}
            onChange={(e) => patch({ to: e.target.value, preset: 'custom' })}
            className="rounded border-gray-300 text-sm"
          />
        </div>

        <button
          onClick={onRefresh}
          disabled={loading}
          className="ml-auto inline-flex items-center gap-1.5 rounded bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          <RotateCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {optionsError && (
        <div className="border-t border-gray-100 pt-2 text-xs text-rose-700">
          Couldn't load the filter lists: {optionsError}. The date range still works.{' '}
          <button onClick={() => setOptionsRetry((n) => n + 1)} className="underline">Try again</button>
        </div>
      )}

      {active.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 border-t border-gray-100 pt-2">
          <span className="text-[11px] uppercase tracking-wide text-gray-400">Scope</span>
          {active.map(([key, label]) => (
            <button
              key={key}
              onClick={() => patch({ [key]: undefined })}
              className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs text-gray-700 hover:bg-gray-100"
              title={`Remove the ${FILTER_NAME[key] ?? key} filter`}
            >
              {label}
              <X className="h-3 w-3 text-gray-400" />
            </button>
          ))}
          <button
            onClick={() => patch({
              org: undefined, agent: undefined, channel: undefined,
              modality: undefined, provider: undefined, feature: undefined,
              campaign: undefined, customer: undefined,
            })}
            className="ml-1 text-xs text-blue-600 hover:underline"
          >
            Clear all
          </button>
        </div>
      )}
    </div>
  );
};

const Select: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  allLabel: string;
  items: [string, string][];
}> = ({ label, value, onChange, allLabel, items }) => (
  <div>
    <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-gray-500">{label}</label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="rounded border-gray-300 py-2 text-sm"
    >
      <option value="">{allLabel}</option>
      {items.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  </div>
);
